import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { LoggedInUser, Token } from '../../../shared/models/logged-in-user';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { ImageService } from '../../../shared/services/image-service/image.service';
import { MicrosoftClarityService } from '../../../shared/services/microsoft-clarity/microsoft-clarity.service';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  Observable,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { DestroyBase } from '../../../shared/base-classes/destroy-component-base';
import { jwtDecode } from 'jwt-decode';
import { EulaType } from '../../license-agreement/models/eula';
import { SessionDetail } from '../../manage-users/models/session-detail';
import { TokenPayload } from '../models/token-payload';
import { RefreshTokenResponse } from '../models/refresh-token';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends DestroyBase {
  router = inject(Router);
  route = inject(ActivatedRoute);
  http = inject(HttpClient);
  loaderService = inject(LoaderService);
  imageService = inject(ImageService);
  auth0 = inject(Auth0Service);
  clarityService = inject(MicrosoftClarityService);

  private readonly KEY_TOKEN = 'accessToken';
  private readonly KEY_USER = 'userInfo';
  private readonly PERMISSIONS = 'permissions';
  private _permissions: string[] = [];
  public urlToRedirectBack = '';

  // Observable to notify when token is refreshed
  private tokenRefreshed$ = new BehaviorSubject<boolean>(false);
  public onTokenRefreshed$ = this.tokenRefreshed$.asObservable();

  constructor() {
    super();
    this._handleAuthRedirect().pipe(takeUntil(this.destroy$)).subscribe();
  }

  logIn(): Observable<void> {
    const currentSiteUrl = window.location.origin;
    const redirectUri = `${currentSiteUrl}/login-callback`;
    return this.auth0.loginWithRedirect({
      authorizationParams: {
        redirect_uri: redirectUri,
        prompt: 'login',
      },
      appState: {
        target:
          this.urlToRedirectBack !== ''
            ? this.urlToRedirectBack
            : this.router.url,
      },
    });
  }

  reAuthenticate(): Observable<void> {
    const currentSiteUrl = window.location.origin;
    const redirectUri = `${currentSiteUrl}/login-callback`;

    localStorage.removeItem(this.KEY_TOKEN);

    // Set a flag in sessionStorage to indicate that re-authentication has been initiated
    sessionStorage.setItem('reauth_initiated', 'true');

    return this.auth0.loginWithRedirect({
      authorizationParams: {
        redirect_uri: redirectUri,
        prompt: 'login',
      },
      appState: {
        target: '/change-password',
      },
    });
  }

  logInWithSecret(secret: string, connection?: string): Observable<void> {
    const currentSiteUrl = window.location.origin;
    const redirectUri = `${currentSiteUrl}/login-callback`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const authorizationParams: any = {
      redirect_uri: redirectUri,
      prompt: 'login',
      org_signup_secret: secret,
    };

    if (connection) {
      authorizationParams.connection = connection;
    }

    return this.auth0.loginWithRedirect({
      authorizationParams,
      appState: {
        target: '/dashboard',
        secret,
      },
    });
  }

  private _exchangeTokens(idToken: string) {
    const url = `${environment.apiBaseUrl}/api/auth/auth0-login`;
    return this.http
      .post<LoggedInUser>(url, { id_token: idToken }, { withCredentials: true })
      .pipe(
        tap((res: LoggedInUser) => {
          const accessToken = res.token!.access;
          if (!accessToken || this.isTokenInvalid(accessToken)) {
            this.clearLocalStore();

            this.router.navigate(['/login']);
            return;
          }

          this._setTokenAndUserInfo(accessToken, res);
        }),
        catchError((error) => {
          console.error('Error exchanging Auth0 token:', error);
          this.clearLocalStore();
          this.router.navigate(['/error']);
          return EMPTY;
        }),
      );
  }

  private _setTokenAndUserInfo(accessToken: string, res: LoggedInUser) {
    this._getLoggedInUserPermissions(accessToken);

    localStorage.setItem(this.KEY_TOKEN, accessToken);
    localStorage.setItem(this.KEY_USER, JSON.stringify(res));

    this.clarityService.identifyUser(res);
  }

  public updateUserOnboard() {
    const userInfo = this.getLoggedInUserInfo();
    if (userInfo) {
      userInfo.existing_user = true;
      localStorage.setItem(this.KEY_USER, JSON.stringify(userInfo));
    }
  }

  public updateConnectionDisplayName(name: string) {
    const userInfo = this.getLoggedInUserInfo();
    if (userInfo) {
      userInfo.connection_display_name = name;
      localStorage.setItem(this.KEY_USER, JSON.stringify(userInfo));
    }
  }

  public updateUserName(userName: string) {
    const userInfo = this.getLoggedInUserInfo();
    if (userInfo) {
      userInfo.name = userName;
      localStorage.setItem(this.KEY_USER, JSON.stringify(userInfo));
    }
  }

  public clearLocalStore() {
    localStorage.removeItem(this.KEY_TOKEN);
    localStorage.removeItem(this.KEY_USER);
    sessionStorage.removeItem('errorPageVisits');
    this.imageService.clear();
    const keepKeys = [
      'leadsStatusPopupDismissedUntil',
      'dashboard_expanded_sections',
      'search_history',
      'deeprootsV2AnnouncementDismissed',
      'infoPopupDismissed',
      MicrosoftClarityService.DAILY_SESSION_STORAGE_KEY,
    ];
    Object.keys(localStorage).forEach((key) => {
      if (!keepKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });
  }

  isLoggedIn(): boolean {
    const idToken = localStorage.getItem(this.KEY_TOKEN);
    return !!idToken && this.isTokenValid(idToken);
  }

  private _handleAuthRedirect() {
    return this.auth0.isAuthenticated$.pipe(
      filter((isAuthenticated) => isAuthenticated),
      switchMap(() => this.auth0.idTokenClaims$),
      switchMap((idTokenClaims) => {
        if (idTokenClaims) {
          return this._exchangeTokens(idTokenClaims.__raw);
        }
        return of(null);
      }),
      switchMap(() => this.auth0.appState$),
      tap((state) => {
        this.urlToRedirectBack = state.target;
        if (this.urlToRedirectBack === '/login') {
          this.urlToRedirectBack = '/dashboard';
        }
        // Handle URL-encoded characters properly
        try {
          // Decode the URL to handle encoded characters like %3F
          const decodedUrl = decodeURIComponent(this.urlToRedirectBack);
          this.router.navigateByUrl(decodedUrl);
        } catch (error) {
          // If URL decoding fails, fallback to dashboard
          console.warn(
            'Failed to decode redirect URL:',
            this.urlToRedirectBack,
            error,
          );
          this.router.navigate(['/dashboard']);
        }
        this.urlToRedirectBack = '';
      }),
    );
  }

  logout() {
    const revokeUrl = `${environment.apiBaseUrl}/api/auth/token/logout/`;
    return this.http.get(revokeUrl).pipe(
      catchError(() => of(null)),
      tap(() => {
        this.clarityService.clearIdentity();
        this.clearLocalStore();

        // Log out from Auth0 to clear the Auth0 session
        const currentSiteUrl = window.location.origin;
        this.auth0.logout({
          logoutParams: {
            returnTo: `${currentSiteUrl}/login`,
          },
        });
      }),
    );
  }

  isTokenValid(token: string): boolean {
    return !this.isTokenInvalid(token);
  }

  isTokenInvalid(token: string): boolean {
    const decodedToken = new JwtHelperService().decodeToken(token);
    return this.hasTokenExpired(decodedToken);
  }

  getStoredToken(): string | null {
    return localStorage.getItem(this.KEY_TOKEN);
  }

  getLoggedInUserInfo(): LoggedInUser | null {
    const storedUser = localStorage.getItem(this.KEY_USER);
    if (storedUser) {
      return JSON.parse(storedUser);
    }

    return null;
  }

  // TODO: use this in the future for places where group was used
  getLoggedInUserConnectionName(): string | undefined {
    const decodedToken: TokenPayload = this._getDecodedToken();
    return decodedToken.connection;
  }

  getCRMAuthToken(): Observable<Token> {
    return this.http.get<Token>(`${environment.apiBaseUrl}/api/auth/crm`);
  }

  getEulaType(token: string): Observable<EulaType> {
    return this.http
      .get<EulaType>(`${environment.apiBaseUrl}/api/auth/eula`, {
        headers: { 'X-API-Key': token },
      })
      .pipe(
        switchMap((res: unknown) => {
          return of((res as { eula: EulaType }).eula || EulaType.Default);
        }),
      );
  }

  private _getDecodedToken(): TokenPayload {
    const accessToken = this.getStoredToken();
    const decodedToken: TokenPayload = jwtDecode(accessToken!);
    return decodedToken;
  }

  private _getLoggedInUserPermissions(accessToken: string): void {
    localStorage.removeItem(this.PERMISSIONS);
    const decodedToken: TokenPayload = jwtDecode(accessToken);
    localStorage.setItem(
      this.PERMISSIONS,
      JSON.stringify(decodedToken.permissions),
    );
  }

  hasPermission(permission: string): boolean {
    if (permission === 'common') {
      return true;
    }

    let _permissions = localStorage.getItem(this.PERMISSIONS);
    _permissions = _permissions ? JSON.parse(_permissions) : [];

    // Handle OR conditions in permissions (separated by |)
    if (permission.includes('|')) {
      const requiredPermissions = permission.split('|');
      return requiredPermissions.some((p) => _permissions!.includes(p));
    }

    // Regular single permission check
    return _permissions!.includes(permission);
  }

  addPermission(permission: string): void {
    const permissions = localStorage.getItem(this.PERMISSIONS);
    const parsedPermissions: string[] = permissions
      ? JSON.parse(permissions)
      : [];
    if (!parsedPermissions.includes(permission)) {
      parsedPermissions.push(permission);
      localStorage.setItem(this.PERMISSIONS, JSON.stringify(parsedPermissions));
    }
  }

  removePermission(permission: string): void {
    const permissions = localStorage.getItem(this.PERMISSIONS);
    if (permissions) {
      const parsedPermissions: string[] = JSON.parse(permissions).filter(
        (p: string) => p !== permission,
      );
      localStorage.setItem(this.PERMISSIONS, JSON.stringify(parsedPermissions));
    }
  }

  refreshToken(): Observable<string> {
    const url = `${environment.apiBaseUrl}/api/auth/token/refresh/`;
    return this.http
      .post<RefreshTokenResponse>(url, {}, { withCredentials: true })
      .pipe(
        tap((res) => {
          this._updateStoredToken(res);
          // Notify subscribers that token has been refreshed
          this.tokenRefreshed$.next(true);
        }),
        switchMap((res) => of(res.access)),
      );
  }

  getUserSessions(userId?: number): Observable<SessionDetail[]> {
    const url = userId
      ? `${environment.apiBaseUrl}/api/auth/sessions/${userId}/`
      : `${environment.apiBaseUrl}/api/auth/sessions/`;
    return this.http.get<SessionDetail[]>(url);
  }

  revokeAllSessions(userId?: number): Observable<unknown> {
    const url = userId
      ? `${environment.apiBaseUrl}/api/auth/token/admin-revoke/${userId}/`
      : `${environment.apiBaseUrl}/api/auth/token/revoke/`;
    return this.http.get(url, { withCredentials: true });
  }

  private _updateStoredToken(res: RefreshTokenResponse) {
    localStorage.setItem(this.KEY_TOKEN, res.access);

    // Update the token inside the user object
    const user = this.getLoggedInUserInfo();
    if (user && user.token) {
      const updatedToken = {
        ...user.token,
        access: res.access,
        token_expiry: res.token_expiry,
      };

      const updatedUser = {
        ...user,
        token: updatedToken,
      };

      localStorage.setItem(this.KEY_USER, JSON.stringify(updatedUser));
      this._getLoggedInUserPermissions(res.access);
    }
  }

  smeSignup(email: string): Observable<unknown> {
    const url = `${environment.apiBaseUrl}/api/sme/user/signup`;
    return this.http.post(url, { email });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private hasTokenExpired(decodedToken: any): boolean {
    try {
      const exp = decodedToken.exp as number;
      const expiry = exp * 1000;
      return Date.now() >= expiry;
    } catch {
      return true;
    }
  }

  isSMEUser(): boolean {
    const loggedInUser = this.getLoggedInUserInfo();
    if (loggedInUser) {
      return (
        loggedInUser.actor_type === 'sme' || loggedInUser.connection === 'sme'
      );
    }
    return false;
  }

  isDHUser(): boolean {
    const connection = this.getLoggedInUserInfo()?.connection;
    return connection?.split('::')[0] === 'dh';
  }

  changePassword(newPassword: string): Observable<void> {
    return this.http.post<void>(
      `${environment.apiBaseUrl}/api/auth/change-password`,
      { new_password: newPassword },
    );
  }
}
